import React, { useState, useContext, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { TextField, Typography, Icon } from "@material-ui/core";
import LockOpenIcon from "@material-ui/icons/LockOpen";
import HelpIcon from "@material-ui/icons/Help";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import { Button } from "../../components/ui/forms/Button";
import LogoLogin from "../../assets/LogoLogin";
import { UserContext } from "../../contexts/UserContext";
import { Copyright } from "./Copyright";
import { useSnackbar } from "notistack";
import { DatabaseContext } from "../../contexts/DatabaseContext";

function Login() {
  const { id, login, getMe, ReSendAuthCode, VerifyCode } =
    useContext(UserContext);
  const { changeToken } = useContext(DatabaseContext);
  const [state, setState] = useState({
    email: "",
    password: "",
    Code: "",
  });
  const [attempts, setAttempts] = useState(0);
  const { email, password, Code } = state;
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [Auth, setAuth] = useState(false);
  const [UserID, setUID] = useState("");
  const { enqueueSnackbar } = useSnackbar();

  // ✅ Automatically redirect logged-in users via useEffect
  useEffect(() => {
    if (id) {
      navigate("/dashboard", { replace: true });
    }
  }, [id, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAttempts((prev) => prev + 1);
    setLoading(true);

    try {
      const loginSuccess = await login(email, password);

      if (loginSuccess?.success === true) {
        localStorage.setItem("token", loginSuccess.token);
        changeToken(loginSuccess.token);
        if (loginSuccess.user?.TwoFactorAuth === true) {
          setAuth(true);
          setUID(loginSuccess.user.id);
          setLoading(false);
        } else {
          await getMe();
          setLoading(false);
          navigate("/dashboard", { replace: true });
        }
      } else {
        setLoading(false);
        enqueueSnackbar(loginSuccess?.message || "Invalid credentials", {
          variant: "error",
        });
      }
    } catch (err) {
      setLoading(false);
      enqueueSnackbar(
        err?.response?.data?.message || "Login failed. Check your credentials.",
        { variant: "error" },
      );
    }
  };

  const handleChange = (e) => {
    const { value, name } = e.target;
    setState((s) => ({ ...s, [name]: value }));
  };

  const VerifyAuthCode = async () => {
    if (UserID && Code) {
      try {
        const res = await VerifyCode(UserID, Code * 5);

        if (res?.data?.[0]?.[0]?.success === "true") {
          await getMe();
          setAuth(false);
          navigate("/dashboard", { replace: true });
        } else {
          enqueueSnackbar("Code is incorrect or expired", {
            variant: "error",
            autoHideDuration: 6000,
          });
        }
      } catch (err) {
        enqueueSnackbar("Verification failed", { variant: "error" });
      }
    }
  };

  const ReSendCode = async () => {
    await ReSendAuthCode(UserID);
  };

  // ✅ Guard Clause: While checking auth or redirecting, return null
  if (id) {
    return null;
  }

  return !Auth ? (
    <Container>
      <Box>
        <LogoLogin />
        <Form onSubmit={handleSubmit}>
          {loading ? (
            <Icon icon={<AutorenewIcon />} spin size="2x" />
          ) : (
            <>
              <Inputs>
                <Input
                  type="email"
                  name="email"
                  onChange={handleChange}
                  label="Email"
                  value={email}
                  size="medium"
                  required
                />
                <Input
                  type="password"
                  name="password"
                  onChange={handleChange}
                  label="Password"
                  value={password}
                  size="medium"
                  required
                />
              </Inputs>
              {attempts > 2 && (
                <Typography variant="button" color="error">
                  Try refreshing the page
                </Typography>
              )}
              <FormButtons>
                <Button
                  startIcon={<HelpIcon />}
                  onClick={() => navigate("/forgot", { replace: true })}
                >
                  Forgot Details
                </Button>
                <LoginButton
                  type="submit"
                  colour="secondary"
                  startIcon={<LockOpenIcon />}
                >
                  Login
                </LoginButton>
              </FormButtons>
            </>
          )}
        </Form>
        <Copyright />
      </Box>
    </Container>
  ) : (
    <DivCenter className="center">
      <Box2>
        <LogoLogin />
        <span style={{ fontSize: 20, color: "white" }}>
          <b>Verify your identity</b>
        </span>
        <span style={{ fontSize: 16, color: "white", marginTop: "2%" }}>
          Please enter the code sent to your email address.
        </span>
        <TextField
          name="Code"
          onChange={handleChange}
          label="Code"
          value={Code}
          style={{ maxWidth: "30%" }}
          required
        />
        <span style={{ fontSize: 16, color: "white", marginTop: "5%" }}>
          Note: This code will only be valid for 10 minutes.
        </span>
        <span style={{ fontSize: 16, color: "white", marginTop: "2%" }}>
          Didn't receive your code?{" "}
          <button type="button" onClick={ReSendCode}>
            Resend
          </button>
        </span>
        <br />
        <Button style={{ color: "white" }} onClick={VerifyAuthCode}>
          Next
        </Button>
      </Box2>
    </DivCenter>
  );
}

const DivCenter = styled.div`
  margin: auto;
  width: 50%;
  padding: 10px;
  margin-top: 10%;
`;

const Box2 = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  .logo {
    max-width: 180px;
    margin-bottom: 40px;
  }
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
`;

const Input = styled(TextField)``;
const Inputs = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
`;

const Box = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  max-width: 420px;
  .logo {
    max-width: 180px;
    margin-bottom: 40px;
  }
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 4rem;
`;

const FormButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2rem;
`;

const LoginButton = styled(Button)`
  margin-left: 1rem;
`;

export default Login;
